import { ReactNode, useEffect, useMemo, useState } from 'react';
import { uniqBy } from 'lodash';

import {
  AssetsFilter,
  DateRangeFilter,
  FilterCheckbox,
  FiltersBar,
  renderAssetsChips,
  renderDateRangeChip,
  useFiltersState,
} from 'src/components/streametric/filters-bar';
import { useAuthentication } from 'src/features/authentication/context';
import { ACCESS_ROLES } from 'src/config';
import { SitesService } from 'src/services/sites/sites.service';

import { FilterItem as FilterItemType } from '../types';

type Props = {
  onChange: (filters: FilterItemType[]) => void;
  /** Rendered on the left of the filters row, e.g. pagination. */
  leading?: ReactNode;
};

const formsGroup = {
  name: 'event_type',
  label: 'Forms',
};
const auditTrial = {
  name: 'audit_trial',
  label: 'Audit Trail',
};
const assets = {
  name: 'assets',
  label: 'Assets',
};
const timeFrame = {
  name: 'time_frame',
  label: 'Time Frame',
};

const LogFilters = ({ onChange, leading }: Props) => {
  const [isFormsLoading, setIsFormsLoading] = useState(true);

  const {
    customerId: { value: customerId },
    siteId: { value: siteId },
    getCurrentRole,
  } = useAuthentication();
  const canDeleteLog = () => {
    const currentRole = getCurrentRole(customerId, siteId);
    const isSuperAdmin = currentRole === ACCESS_ROLES.SUPER_ADMIN;
    const isAccountAdmin = currentRole === ACCESS_ROLES.ACCOUNT_ADMIN;
    const isSiteAdmin = currentRole === ACCESS_ROLES.SITE_MANAGER;

    return isSuperAdmin || isAccountAdmin || isSiteAdmin;
  };

  const { filtersState, setFiltersState, appliedFilters, removeFilter, updateFilters, clearAll } =
    useFiltersState(
      () =>
        [
          {
            name: 'time_frame',
            label: 'Time Frame',
            component: DateRangeFilter,
            group: timeFrame,
            renderChip: renderDateRangeChip,
            value: '',
          },
          {
            name: 'deleted_logs',
            label: 'Deleted logs',
            component: FilterCheckbox,
            value: false,
            group: auditTrial,
          },
          {
            name: 'assets',
            label: 'Assets',
            component: AssetsFilter,
            value: [],
            group: assets,
            renderChip: renderAssetsChips,
          },
        ].filter((item) => (item.name === 'deleted_logs' && !canDeleteLog() ? false : true)),
      onChange
    );

  useEffect(() => {
    (async () => {
      setIsFormsLoading(true);
      try {
        const data = await SitesService.getAssignedForms(customerId!, siteId!);

        if (Array.isArray(data) && data.length) {
          setFiltersState((prevState) =>
            uniqBy(
              [
                ...prevState,
                ...data.map((form) => ({
                  name: form.id,
                  label: form.name,
                  component: FilterCheckbox,
                  value: false,
                  group: formsGroup,
                })),
              ],
              'name'
            )
          );
        }

        setIsFormsLoading(false);
      } catch (err) {
        console.error(err);
        setIsFormsLoading(false);
      }
    })();
  }, [customerId, siteId, setFiltersState, setIsFormsLoading]);

  // "Deleted logs" is always the last group in the drawer.
  const visibleFilters = useMemo(() => {
    const nonDeletedLogs = filtersState.filter((f) => f.name !== 'deleted_logs');
    const deletedLogFilter = filtersState.find((f) => f.name === 'deleted_logs');

    return deletedLogFilter ? [...nonDeletedLogs, deletedLogFilter] : nonDeletedLogs;
  }, [filtersState]);

  return (
    <FiltersBar
      filters={visibleFilters}
      appliedFilters={appliedFilters}
      onApply={updateFilters}
      removeFilter={removeFilter}
      onClearAll={clearAll}
      isFormsLoading={isFormsLoading}
      leading={leading}
    />
  );
};

export default LogFilters;
